import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
} from "react-native";
import React, { useEffect, useState } from "react";
import { AppColors } from "../../../utility/AppColors";
import { toast } from "react-toastify";
import { base_url, endpoints } from "../../../Api/configs";
import { getLoginData } from "../../../utility/AppStoreData";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  RouteProp,
  StackActions,
  useNavigation,
  useRoute,
  useFocusEffect,
} from "@react-navigation/native";
import { navigateToScreen } from "../../../utility/NavigationHelper";
import { AppScreens } from "../../Utility/AppScreens";
import { AppFontStyle } from "../../../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import SubscriptionTabComponent from "../../../Components/settings-common-components/SubscriptionTabComponent";
import ChangePasswordTabComponent from "../../../Components/settings-common-components/ChangePasswordTabComponent";

import Separator from "../../../Components/Separator";
import AccountTabComponent from "../../../Components/settings-common-components/AccountTabComponent";
import { getUserProfile } from "../../../services/authAPI";
import DeactivateTabComponent from "../../../Components/settings-common-components/DeactivateTabComponent";
import { AppImages } from "../../../utility/AppImages";
import SettingMenuComponent from "../../../Components/settings-common-components/SettingMenuComponent";
import BlockReportTabComponent from "../../../Components/settings-common-components/BlockReportTabComponent";
import StreamSettingsTabComponent from "../../../Components/settings-common-components/StreamSettingsTabComponent";
import Header from "./Common/Header";
import StreamSetupPopup from "../../../Components/settings-common-components/StreamSetupPopup";
import { clearAllCaches } from "../../../utility/cacheManager";
import { ScreenBGGradientView } from "../../../common/CommonView";

type SettingScreenRouteProp = RouteProp<{ params: { tab?: string } }, "params">;

const SettingWebScreen = () => {
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();
  const [accessToken, setAccessToken] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);

  const styles = useStyles();
  const route = useRoute<SettingScreenRouteProp>();

  // Initialize from route params or URL query string
  const getInitialTab = () => {
    if (route.params?.tab) return route.params.tab;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromUrl = urlParams.get('tab');
      if (tabFromUrl) return tabFromUrl;
    }
    return "account";
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  async function getLoginUser() {
    const data = await getLoginData();
    if (data != null) {
      setAccessToken(data.AccessToken);
    }
  }

  // Fix URL every time screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;

        console.log(`[SettingWebScreen] Focus - currentPath: ${currentPath}, currentSearch: ${currentSearch}, selectedTab: ${selectedTab}`);

        // Fix capitalized or old screen name URLs
        if (currentPath === '/SettingWebScreen' || currentPath === '/Settings') {
          const newUrl = `/settings${currentSearch || `?tab=${selectedTab}`}`;
          window.history.replaceState(null, '', newUrl);
          console.log(`[SettingWebScreen] Fixed capitalized URL to: ${newUrl}`);
          return; // Exit early, URL will be corrected on next render
        }

        // Ensure we're on the settings path and tab parameter is present
        if (currentPath === '/settings' || currentPath === '/SettingWebScreen' || currentPath === '/Settings') {
          const urlParams = new URLSearchParams(currentSearch);
          const tabFromUrl = urlParams.get('tab');

          if (!tabFromUrl) {
            const newUrl = `/settings?tab=${selectedTab}`;
            window.history.replaceState(null, '', newUrl);
            console.log(`[SettingWebScreen] Added missing tab parameter: ${newUrl}`);
          }
        }
      }
    }, [selectedTab])
  );

  useEffect(() => {
    getLoginUser();
  }, []);

  const getLoginUserData = async () => {
    try {
      const data = await getLoginData();
      if (data != null) {
        // Clear all caches first - user should be logged out locally regardless of API response
        await clearAllCaches();

        const params = { accessToken: data.AccessToken };

        // Try to call logout API, but don't depend on it for local logout
        userLogoutAPI({
          bodyParams: params,
          success: (responseData) => {
            toast.success(responseData.message);
          },
        });

        // Always redirect to login screen after clearing local data
        navigation.dispatch(StackActions.replace(AppScreens.LoginWebScreen));
      }
    } catch (error) {
      console.error("Logout error:", error);
      // Even if there's an error, still redirect to login since we cleared local data
      navigation.dispatch(StackActions.replace(AppScreens.LoginWebScreen));
      toast.error("Logged out locally");
    }
  };

  // Update tab when route params change
  useEffect(() => {
    const tabFromUrl = route.params?.tab;
    if (tabFromUrl && tabFromUrl !== selectedTab) {
      setSelectedTab(tabFromUrl);
    }
  }, [route.params?.tab]);

  useEffect(() => {
    if (selectedTab) {
      // Update URL manually to use /settings instead of /SettingWebScreen
      if (typeof window !== 'undefined') {
        const newUrl = `/settings?tab=${selectedTab}`;
        const currentUrl = window.location.pathname + window.location.search;
        // Only update if URL is different
        if (currentUrl !== newUrl) {
          window.history.replaceState(null, '', newUrl);
          console.log(`[SettingWebScreen] URL updated to: ${newUrl}`);
        }
      }
    }
  }, [selectedTab]);

  const renderContent = () => {
    switch (selectedTab) {
      case "account":
        return <AccountTabComponent accessToken={accessToken} />;
      case "subscription":
        return <SubscriptionTabComponent />;
      case "changePassword":
        return <ChangePasswordTabComponent accessToken={accessToken} />;
      case "streamSettings":
        return <StreamSettingsTabComponent accessToken={accessToken} />;
      case "deactivate":
        return (
          <DeactivateTabComponent
            accessToken={accessToken}
            getLoginUserData={getLoginUserData}
          />
        );
      case "blockReportUsers":
        return <BlockReportTabComponent accessToken={accessToken} />;
      default:
        return <AccountTabComponent accessToken={accessToken} />;
    }
  };

  const tabs = [
    { key: "account", label: "Account" },
    { key: "subscription", label: "Subscription" },
    { key: "changePassword", label: "Change Password" },
    { key: "streamSettings", label: "Stream Settings" },
    { key: "deactivate", label: "Deactivate Account" },
    { key: "blockReportUsers", label: "Block/Report users" },
  ];

  const toggleMenu = () => {
    setMenuVisible(!menuVisible);
  };

  return (
    <View style={styles.container}>
      <ScreenBGGradientView />
      <Header
        btnSearchTapped={() => {
          navigateToScreen(navigation, AppScreens.SearchEventWebScreen);
        }}
        btnWalletTapped={() => { }}
        btnStartTutorialTapped={() => { }}
        btnMessageTapped={() => { }}
        btnNotificationTapped={() => { }}
        btnProfileTapped={() => { }}
      />

      <View style={styles.settingsContainer}>
        <Text style={[AppFontStyle.BOLD_ANYBODY_32, {}]}>Settings</Text>

        <Separator
          color={AppColors.gray_1E1E1E}
          height={2}
          marginTop={20}
          marginBottom={20}
          marginHorizontal={0}
        />

        <View style={styles.innerContainer}>
          {deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS ? (
            <View
              style={{
                backgroundColor: AppColors.purple_453B50,
                shadowColor: AppColors.purple_B155FF,
                shadowOffset: { width: 0, height: -4 },
                shadowOpacity: 1,
                shadowRadius: 4,
                elevation: 8,
                borderRadius: 10,
                paddingVertical: 6,
                paddingHorizontal: 20,
                zIndex: 10,
                position: "relative",
                marginTop: 10,
              }}
            >
              <TouchableOpacity
                onPress={toggleMenu}
                style={{ flexDirection: "row", alignItems: "center", gap: 13 }}
              >
                <Image
                  source={AppImages.SettingIcon}
                  style={{
                    borderColor: AppColors.red_912121,
                    width: 36,
                    height: 36,
                    marginVertical: 5,
                  }}
                />
                <Text style={[AppFontStyle.BOLD_ANYBODY_14]}>
                  {selectedTab
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </Text>
              </TouchableOpacity>

              {menuVisible && (
                <ScrollView
                  contentContainerStyle={{
                    padding: 10,
                  }}
                  style={{
                    position: "absolute",
                    top: 50,
                    zIndex: 20,
                    backgroundColor: AppColors.black,
                    borderRadius: 10,
                    shadowColor: AppColors.gray_6E90B4,
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 1,
                    shadowRadius: 4,
                    elevation: 8,
                    // height: 400,
                  }}
                >
                  <SettingMenuComponent
                    tabs={tabs}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    styles={styles}
                    toggleMenu={toggleMenu}
                  />
                </ScrollView>
              )}
            </View>
          ) : (
            <SettingMenuComponent
              tabs={tabs}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              styles={styles}
              toggleMenu={() => { }}
            />
          )}

          <ScrollView
            style={styles.contentContainer}
            showsVerticalScrollIndicator={true}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
          >
            {renderContent()}
          </ScrollView>
        </View>
      </View>
    </View>
  );

  function userLogoutAPI(props) {
    const { bodyParams = {}, success } = props;

    return fetch(base_url + endpoints.auth.logout, {
      mode: "cors",
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        body: bodyParams,
      }),
    })
      .then(async function (data) {
        const responce = await data.json();
        const statusCode = data.status;
        if (statusCode === 200) {
          success(responce.body);
        } else if (statusCode === 400) {
          toast.error("Bad Request");
        } else if (statusCode > 400 && statusCode < 420) {
          toast.error("Unauthorized");
        } else if (statusCode >= 500) {
          toast.error("Internal Server Error");
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  }
};

export default SettingWebScreen;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
    },
    settingsContainer: {
      paddingHorizontal: "3.5%",
      paddingTop: "2.5%",
      flex: 1,
    },
    innerContainer: {
      flex: 1,
      flexDirection: "row",
      gap: 50,
    },
    contentContainer: {
      flex: 1,
    },
    scrollContent: {
      paddingBottom: 50,
    },
    contentText: {
      color: AppColors.white,
      fontSize: 16,
    },
    buttonInner: {
      paddingVertical: 10,
      paddingHorizontal: 12,
    },
    buttonStyle: {
      borderColor: AppColors.grayColor_505050,
      borderWidth: 1,
      borderRadius: 6,
      width: 200,
      marginBottom: 10,
    },
    topLineStyle: {
      width: "100%",
      height: 1,
      backgroundColor: AppColors.grayColor_505050,
    },

    buttonTextStyle: {
      // marginTop: 7,
      // paddingTop: 3,
      color: AppColors.white,
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {
      innerContainer: {
        flexDirection: "column",
        gap: 30,
      },
    },
    [DEVICE_SIZES.XS]: {
      innerContainer: {
        flexDirection: "column",
        gap: 30,
      },
    },
  }
);
